import { CalendarX } from 'lucide-react';
import type { InterviewOutcomes } from '../../services/usageApi';
import { StatTile } from './StatTile';
import { RankedBars } from './RankedBars';
import { OutcomeChangesSection } from './OutcomeChangesSection';
import {
  SERIES_ERROR,
  SERIES_PRIMARY,
  formatCompact,
  formatHours,
  formatPercent,
  humaniseCode,
} from './chartTokens';

/**
 * What happened to the interviews that got booked.
 *
 * Built from booking RECORDS, so a cancellation counted here actually happened — unlike the
 * candidate-side `booking_cancelled` event, which is real but never reaches this sink because
 * candidates are signed out.
 *
 * Only reason CODES appear. The stored `cancelReason` field also contains whatever the candidate
 * typed into the note box, and that text is theirs — it is not aggregated, charted, or shown here.
 */
export function OutcomesSection({ outcomes }: { outcomes: InterviewOutcomes }) {
  const { totals } = outcomes;

  const undated = totals.undatedBookings ?? 0;

  if (totals.booked === 0) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-300 bg-white p-8 text-center">
        <CalendarX className="mx-auto h-8 w-8 text-zinc-300" />
        <p className="mt-3 text-sm font-medium text-zinc-700">No interviews booked in this range</p>
        {/*
          The distinction that matters when this panel is empty: nothing booked, or bookings that
          cannot be dated? Until Aug 2026 cancelling a booking dropped its `bookedAt`, so a real
          booking-and-cancellation showed up here as no activity at all.
        */}
        {undated > 0 && (
          <p className="mt-1 text-xs text-zinc-500">
            {undated} booking{undated === 1 ? '' : 's'} could not be dated and {undated === 1 ? 'is' : 'are'}{' '}
            not counted — these were cancelled before the record kept its booking time.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="Booked"
          value={formatCompact(totals.booked)}
          hint={undated > 0 ? `${undated} undated, not counted` : 'Interviews confirmed'}
        />
        <StatTile
          label="Cancelled"
          value={totals.cancelRate === null ? '—' : formatPercent(totals.cancelRate)}
          hint={`${formatCompact(totals.cancelled)} interviews`}
        />
        <StatTile
          label="Rescheduled"
          value={totals.rescheduleRate === null ? '—' : formatPercent(totals.rescheduleRate)}
          hint={`${totals.rescheduledTwicePlus} moved more than once`}
        />
        <StatTile
          label="Median lead time"
          value={totals.medianLeadDays === null ? '—' : `${totals.medianLeadDays}d`}
          hint="Booking to interview"
        />
      </div>

      {/*
        Two different clocks, deliberately both shown.

        "After the invite email" starts when the candidate was actually ASKED, so it measures THEIR
        responsiveness. "From link created" starts when the recruiter generated the link, which can
        sit unsent for days — that one measures our latency and theirs together. Presenting either
        alone would answer a question nobody asked.
      */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="Booked after invite email"
          value={formatHours(totals.medianBookAfterEmailHours)}
          hint={
            totals.bookAfterEmailSampleSize === 0
              ? 'No sent invitations matched yet'
              : `Median · ${formatCompact(totals.bookAfterEmailSampleSize)} measured`
          }
        />
        <StatTile
          label="Slowest 10% to book"
          value={formatHours(totals.p90BookAfterEmailHours)}
          hint="p90 — the tail"
        />
        <StatTile
          label="From link created"
          value={totals.medianScheduleDays === null ? '—' : `${totals.medianScheduleDays}d`}
          hint={
            totals.scheduleSampleSize < 10
              ? `Only ${totals.scheduleSampleSize} measured — not a trend`
              : 'Includes time the link sat unsent'
          }
        />
      </div>

      {/* Sorted by cancellations rather than volume: this table exists to locate where the process
          loses people, and the busiest stage is rarely the worst one. */}
      {outcomes.byStage.length > 0 && (
        <section className="rounded-xl border border-zinc-200 bg-white p-5">
          <h3 className="text-sm font-semibold text-zinc-900">Where interviews fall over</h3>
          <p className="mt-0.5 text-xs text-zinc-500">
            By stage, worst first. A stage cancelling far above the others is a process problem with
            an address.
          </p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-zinc-200 text-xs text-zinc-500">
                  <th scope="col" className="py-2 pr-4 font-medium">Stage</th>
                  <th scope="col" className="py-2 pr-4 text-right font-medium">Booked</th>
                  <th scope="col" className="py-2 pr-4 text-right font-medium">Cancelled</th>
                  <th scope="col" className="py-2 pr-4 text-right font-medium">Moved</th>
                  <th scope="col" className="py-2 text-right font-medium">Cancel rate</th>
                </tr>
              </thead>
              <tbody style={{ fontVariantNumeric: 'tabular-nums' }}>
                {outcomes.byStage.map((stage) => (
                  <tr key={stage.stage} className="border-b border-zinc-100 last:border-0">
                    <td className="py-1.5 pr-4 text-zinc-700">{stage.stage}</td>
                    <td className="py-1.5 pr-4 text-right text-zinc-600">{stage.booked}</td>
                    <td className="py-1.5 pr-4 text-right text-zinc-900">{stage.cancelled}</td>
                    <td className="py-1.5 pr-4 text-right text-zinc-600">{stage.rescheduled}</td>
                    <td className="py-1.5 text-right text-zinc-900">
                      {stage.cancelRate === null ? '—' : formatPercent(stage.cancelRate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/*
        Who did it, and how — candidate vs FINN, with reasons split the same way. The pooled tables
        below are the fallback for a proxy answering without `changes`; with it, the split replaces
        them (the pooled "Why candidates cancelled" listed staff codes as candidates' reasons).
      */}
      {outcomes.changes ? (
        <OutcomeChangesSection changes={outcomes.changes} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="rounded-xl border border-zinc-200 bg-white p-5">
            <h3 className="text-sm font-semibold text-zinc-900">Why interviews were cancelled</h3>
            <p className="mt-0.5 text-xs text-zinc-500">
              Candidate and FINN reasons pooled — this proxy build does not yet split them.
            </p>
            <div className="mt-3">
              <RankedBars
                items={outcomes.cancelReasons.map((r) => ({ label: humaniseCode(r.code), value: r.count }))}
                color={SERIES_ERROR}
                emptyMessage="No cancellations in this range."
              />
            </div>
          </section>

          <section className="rounded-xl border border-zinc-200 bg-white p-5">
            <h3 className="text-sm font-semibold text-zinc-900">Why interviews were moved</h3>
            <p className="mt-0.5 text-xs text-zinc-500">Reschedule reasons, most common first.</p>
            <div className="mt-3">
              <RankedBars
                items={outcomes.rescheduleReasons.map((r) => ({ label: humaniseCode(r.code), value: r.count }))}
                color={SERIES_PRIMARY}
                emptyMessage="Nothing was rescheduled."
              />
            </div>
          </section>
        </div>
      )}

      <p className="text-xs text-zinc-400">
        “Booked after invite email” covers only invitations sent <em>through the board</em> — a link
        pasted into someone’s own mail client leaves no send record, which is what the sample size
        beside it is for. Where a candidate was re-invited, the clock runs from the LATEST invitation
        before the booking, not the expired first one.
        <br />
        Reason <strong className="font-medium text-zinc-500">codes</strong> only. Candidates can also
        type a note when they cancel; that text is theirs and is never aggregated here.
        {totals.cancelReasonMissing > 0 &&
          ` ${totals.cancelReasonMissing} cancellation(s) carried no code and are shown as “No reason given”.`}
      </p>
    </div>
  );
}
